// Generic Table Sort functionality with number formatting support
export class TableSort {
  constructor() {
    this.init()
  }

  init() {
    document.addEventListener('turbo:load', () => {
      this.attachSortListeners()
    })

    // Turbo Frame で差し込まれた表は turbo:load が来ないので、読み込み後に初期化する
    document.addEventListener('turbo:frame-load', () => {
      this.attachSortListeners()
    })

    // Turbo Stream で差し込まれた表(カテゴリー別分析の保存後の再描画など)は turbo:load が来ないので、描画のあとに初期化する
    document.addEventListener('turbo:before-stream-render', (event) => {
      const originalRender = event.detail.render
      event.detail.render = async (streamElement) => {
        await originalRender(streamElement)
        this.attachSortListeners()
      }
    })

    // キャッシュのスナップショットは属性ごと複製されるがリスナーは複製されない。
    // 戻る操作で復元した表を初期化し直せるよう、キャッシュ前に印を外す
    document.addEventListener('turbo:before-cache', () => {
      document.querySelectorAll('table[data-sort-initialized]').forEach(table => {
        delete table.dataset.sortInitialized
      })
    })
  }

  attachSortListeners() {
    // data-sortable属性を持つテーブルを処理
    document.querySelectorAll('table[data-sortable]').forEach(table => {
      this.initializeTable(table)
    })
  }

  initializeTable(table) {
    // 同じ表に2回リスナーを付けない(Turbo Stream のたびにページ内の全表を走査するため)
    if (table.dataset.sortInitialized) return
    table.dataset.sortInitialized = 'true'

    this.storeOriginalRanks(table)

    const headers = table.querySelectorAll('thead th[data-sort]')
    
    headers.forEach(header => {
      header.style.cursor = 'pointer'
      header.classList.add('select-none') // テキスト選択を防ぐ
      
      // ソートアイコンを追加
      if (!header.querySelector('.sort-icon')) {
        const icon = document.createElement('span')
        icon.className = 'sort-icon iconify lucide--chevrons-up-down ml-2 text-sm opacity-50'
        header.appendChild(icon)
      }

      header.addEventListener('click', () => {
        this.sortTable(table, header)
      })
    })

    // 初期ソート設定をチェック
    this.applyInitialSort(table)
  }

  applyInitialSort(table) {
    // data-default-sort属性を持つヘッダーを探す
    const defaultSortHeader = table.querySelector('thead th[data-default-sort]')
    
    if (defaultSortHeader) {
      const defaultDirection = defaultSortHeader.getAttribute('data-default-sort') // 'asc' or 'desc'
      
      // 初期ソート状態を設定
      defaultSortHeader.setAttribute('data-direction', defaultDirection === 'desc' ? 'asc' : 'desc')
      
      // アイコンを更新
      const icon = defaultSortHeader.querySelector('.sort-icon')
      if (icon) {
        icon.className = `sort-icon iconify ${defaultDirection === 'asc' ? 'lucide--chevron-up' : 'lucide--chevron-down'} ml-2 text-sm`
      }
      
      // ソートを実行（direction は反転されるので、期待する方向になる）
      this.sortTable(table, defaultSortHeader)
    }
  }

  sortTable(table, clickedHeader) {
    const tbody = table.querySelector('tbody')
    const rows = Array.from(tbody.querySelectorAll('tr')).filter(row => 
      !row.classList.contains('hidden') || row.classList.contains('influencer-extra')
    )
    
    const columnIndex = this.getColumnIndex(clickedHeader)
    const sortType = clickedHeader.getAttribute('data-sort')
    const currentDirection = clickedHeader.getAttribute('data-direction')
    const newDirection = !currentDirection
      ? (sortType === 'rank' ? 'asc' : 'desc')
      : currentDirection === 'asc' ? 'desc' : 'asc'

    // 他のヘッダーのソート状態をリセット
    table.querySelectorAll('thead th[data-sort]').forEach(header => {
      header.removeAttribute('data-direction')
      const icon = header.querySelector('.sort-icon')
      if (icon) {
        icon.className = 'sort-icon iconify lucide--chevrons-up-down ml-2 text-sm opacity-50'
      }
    })

    // 現在のヘッダーのソート状態を更新
    clickedHeader.setAttribute('data-direction', newDirection)
    const icon = clickedHeader.querySelector('.sort-icon')
    if (icon) {
      icon.className = `sort-icon iconify ${newDirection === 'asc' ? 'lucide--chevron-up' : 'lucide--chevron-down'} ml-2 text-sm`
    }

    // ソート実行
    const sortedRows = this.performSort(rows, columnIndex, sortType, newDirection)

    // テーブルを更新
    sortedRows.forEach(row => tbody.appendChild(row))

    // もとの順位は変更しないため再採番はしない
  }

  getColumnIndex(header) {
    const headers = Array.from(header.parentNode.children)
    return headers.indexOf(header)
  }

  performSort(rows, columnIndex, sortType, direction) {
    return rows.sort((a, b) => {
      const aCell = a.children[columnIndex]
      const bCell = b.children[columnIndex]
      
      let aValue = this.extractSortValue(aCell, sortType)
      let bValue = this.extractSortValue(bCell, sortType)

      let comparison = 0

      switch (sortType) {
        case 'rank':
          const aRank = parseInt(a.dataset.originalRank || '0', 10)
          const bRank = parseInt(b.dataset.originalRank || '0', 10)
          comparison = aRank - bRank
          break
        case 'number':
        case 'percentage':
          // 数値として比較（カンマ区切り、パーセンテージ対応）
          const aNum = this.parseNumber(aValue)
          const bNum = this.parseNumber(bValue)
          comparison = aNum - bNum
          break
        case 'date':
          // 数値のみの文字列の場合はUnixタイムスタンプ（秒）として扱う
          if (/^\d+$/.test(aValue) && /^\d+$/.test(bValue)) {
            comparison = parseInt(aValue, 10) - parseInt(bValue, 10)
          } else {
            comparison = new Date(aValue) - new Date(bValue)
          }
          break
        case 'text':
        default:
          comparison = aValue.localeCompare(bValue, 'ja', { numeric: true })
          break
      }

      return direction === 'asc' ? comparison : -comparison
    })
  }

  extractSortValue(cell, sortType) {
    // data-sort-value属性があればそれを使用
    const sortValue = cell.getAttribute('data-sort-value')
    if (sortValue !== null) {
      // 日付タイプの場合、数値文字列をタイムスタンプとして扱う
      if (sortType === 'date' && /^\d+$/.test(sortValue)) {
        // 数値のみの場合、秒単位のタイムスタンプとして返す（比較時に数値として扱われる）
        return sortValue
      }
      return sortValue
    }

    // テキストコンテンツから値を抽出
    let text = cell.textContent.trim()

    switch (sortType) {
      case 'number':
      case 'percentage':
        return text
      case 'date':
        return text
      case 'text':
      default:
        return text.toLowerCase()
    }
  }

  parseNumber(value) {
    if (!value || value === '') return 0
    
    // 文字列から数値を抽出
    // カンマ、%、円マーク、スペースなどを除去
    let cleanValue = value.toString()
      .replace(/[,\s円¥]/g, '') // カンマ、スペース、円マーク、円記号を除去
      .replace(/%$/, '')         // 末尾の%を除去
      .replace(/[^\d.-]/g, '')   // 数字、小数点、マイナス以外を除去
    
    // 空文字列の場合は0を返す
    if (cleanValue === '' || cleanValue === '-') return 0
    
    const num = parseFloat(cleanValue)
    return isNaN(num) ? 0 : num
  }

  storeOriginalRanks(table) {
    const tbody = table.querySelector('tbody')
    if (!tbody) return

    Array.from(tbody.querySelectorAll('tr')).forEach((row, index) => {
      if (!row.dataset.originalRank) {
        row.dataset.originalRank = (index + 1).toString()
      }
    })
  }
}

// 自動初期化
new TableSort()
